import { readComment, readReport } from '@faceup/crypto'
import { usePromiseSuspense } from '@faceup/ui'
import { useCryptoErrorHandler } from '../utils'

type SuspenseChatMessageArgs = {
  ciphertext: string
  nonce: string
  encryptionKey: string
  /**
   * Report body needed to decrypt the sender name. Only set it for messages
   * authored by the (anonymous) reporter — for member-authored messages the
   * name is known from the GraphQL data and no decryption is required.
   */
  victimReport: { body: string; bodyNonce: string } | null
}

type ChatMessageContent = {
  text: string
  victimName: string | null
}

/**
 * Suspends while a chat message (and, when needed, the reporter's name) is
 * decrypted. Unlike {@link useSuspenseDecrypt}, decryption errors are swallowed
 * into empty/null fallbacks (plus a notification) rather than thrown — a single
 * unreadable message must not blow up the whole chat/export via the error
 * boundary, matching the previous per-message useEffect behaviour.
 *
 * Decryption is cached by ciphertext/nonce/key, so already-decrypted messages
 * resolve synchronously on re-render (e.g. when a new message arrives).
 */
export const useSuspenseChatMessage = ({
  ciphertext,
  nonce,
  encryptionKey,
  victimReport,
}: SuspenseChatMessageArgs): ChatMessageContent => {
  const handleError = useCryptoErrorHandler()
  return usePromiseSuspense(
    async (
      ciphertext: string,
      nonce: string,
      encryptionKey: string,
      victimBody: string | null,
      victimNonce: string | null
    ) => {
      const [commentResult, reportResult] = await Promise.all([
        readComment(ciphertext, nonce, encryptionKey),
        victimBody !== null ? readReport(victimBody, victimNonce ?? '', encryptionKey) : null,
      ])

      let text = ''
      if (commentResult.isErr()) {
        handleError(commentResult.error.message)
      } else {
        text = commentResult.value
      }

      let victimName: string | null = null
      if (reportResult) {
        if (reportResult.isErr()) {
          handleError(reportResult.error.message)
        } else {
          victimName = reportResult.value.victimName ?? null
        }
      }

      return { text, victimName }
    },
    [ciphertext, nonce, encryptionKey, victimReport?.body ?? null, victimReport?.bodyNonce ?? null]
  )
}

/**
 * Text-only variant of {@link useSuspenseChatMessage} for internal comments,
 * which never need to decrypt a reporter name.
 */
export const useSuspenseCommentText = ({
  ciphertext,
  nonce,
  encryptionKey,
}: Omit<SuspenseChatMessageArgs, 'victimReport'>): string => {
  const handleError = useCryptoErrorHandler()
  return usePromiseSuspense(
    async (ciphertext: string, nonce: string, encryptionKey: string) => {
      const result = await readComment(ciphertext, nonce, encryptionKey)
      if (result.isErr()) {
        handleError(result.error.message)
        return ''
      }
      return result.value
    },
    [ciphertext, nonce, encryptionKey]
  )
}
